import { useCallback } from "react"

import type { DynamicDateRange } from "@/components/date-range/DynamicDateRangeInput"
import {
  calculatePresetDateRange,
  customDateRangeToDateRange,
  dynamicDateRangeToDateRange,
  validateCustomDateRange,
  validateDynamicDateRange,
} from "@/utils/date/dateRange"
import type {
  CustomDateRange,
  DateRangePreset,
} from "@/utils/date/dateRangeTypes"

import { useDashboardFilters } from "../contexts/DashboardFiltersContext"
import type { GlobalDateRange } from "../types/core"
import { getAllPresetConfigs } from "../utils/dateRangePresets"

export const useGlobalDateRangeOverride = () => {
  const { state, handlerSetGlobalOverride, handlerClearGlobalOverride } =
    useDashboardFilters()

  // Set global override with a preset
  const setGlobalOverride = useCallback(
    (presetKey: DateRangePreset) => {
      try {
        const dateRange = calculatePresetDateRange(presetKey)
        handlerSetGlobalOverride(dateRange, {
          kind: "preset",
          preset: presetKey,
        })
      } catch {
        // Silent fail
      }
    },
    [handlerSetGlobalOverride],
  )

  // Set global override with custom date range
  const setCustomGlobalOverride = useCallback(
    (dateRange: GlobalDateRange) => {
      handlerSetGlobalOverride(dateRange)
    },
    [handlerSetGlobalOverride],
  )

  // Set global override with custom date range from modal
  const setCustomDateRangeOverride = useCallback(
    (customRange: CustomDateRange) => {
      // Validate the custom date range
      const validation = validateCustomDateRange(
        customRange.startDate,
        customRange.endDate,
        customRange.type,
      )

      if (!validation.isValid) {
        return validation.error || "Invalid custom date range"
      }

      // Convert to GlobalDateRange and set override
      const dateRange = customDateRangeToDateRange(customRange)
      const globalDateRange: GlobalDateRange = {
        start: dateRange.start,
        end: dateRange.end,
      }

      handlerSetGlobalOverride(globalDateRange)
      return null
    },
    [handlerSetGlobalOverride],
  )

  // Set global override with dynamic date range from modal
  const setDynamicDateRangeOverride = useCallback(
    (dynamicRange: DynamicDateRange) => {
      // Validate the dynamic date range
      const validation = validateDynamicDateRange(
        dynamicRange.value,
        dynamicRange.unit,
      )

      if (!validation.isValid) {
        return validation.error || "Invalid dynamic date range"
      }

      // Convert to GlobalDateRange and set override
      const dateRange = dynamicDateRangeToDateRange(dynamicRange)
      const globalDateRange: GlobalDateRange = {
        start: dateRange.start,
        end: dateRange.end,
      }

      handlerSetGlobalOverride(globalDateRange, {
        kind: "dynamic",
        amount: dynamicRange.value,
        unit: dynamicRange.unit,
      })
      return null
    },
    [handlerSetGlobalOverride],
  )

  // Clear global override
  const clearGlobalOverride = useCallback(() => {
    handlerClearGlobalOverride()
  }, [handlerClearGlobalOverride])

  // Check if override is active
  const isOverrideActive = state.isOverrideActive

  // Get current override date range
  const currentOverride = state.globalDateRangeOverride

  // Get display label for current date range
  const getOverrideDisplayLabel = useCallback(() => {
    if (!currentOverride) {
      return "No date range"
    }

    // Try to match with a preset
    const allPresets = getAllPresetConfigs()
    const matchingPreset = allPresets.find((preset) => {
      try {
        const presetRange = calculatePresetDateRange(preset.key)
        return (
          presetRange.start.getTime() === currentOverride.start.getTime() &&
          presetRange.end.getTime() === currentOverride.end.getTime()
        )
      } catch {
        return false
      }
    })

    if (matchingPreset) {
      return matchingPreset.label
    }

    // Check for common dynamic patterns
    const today = new Date()
    const daysDiff = Math.ceil(
      (today.getTime() - currentOverride.start.getTime()) /
        (1000 * 60 * 60 * 24),
    )

    // Check for "Last N days" patterns
    if (daysDiff > 0 && daysDiff <= 365) {
      const endOfToday = new Date(today)
      endOfToday.setHours(23, 59, 59, 999)

      // Check if end date is approximately today
      if (
        Math.abs(currentOverride.end.getTime() - endOfToday.getTime()) <
        86400000
      ) {
        // Within 24 hours
        if (daysDiff === 7) {
          return "Last 7 days"
        }
        if (daysDiff === 14) {
          return "Last 14 days"
        }
        if (daysDiff === 30) {
          return "Last 30 days"
        }
        if (daysDiff === 90) {
          return "Last 90 days"
        }
        if (daysDiff <= 31) {
          return `Last ${daysDiff} days`
        }
      }
    }

    // Custom range display
    const startStr = currentOverride.start.toLocaleDateString()
    const endStr = currentOverride.end.toLocaleDateString()
    return `${startStr} - ${endStr}`
  }, [currentOverride])

  return {
    // State
    isOverrideActive,
    currentOverride,
    currentRelativeOverride: state.relativeDateRangeOverride,

    // Actions
    setGlobalOverride,
    setCustomGlobalOverride,
    setCustomDateRangeOverride,
    setDynamicDateRangeOverride,
    clearGlobalOverride,

    // Utilities
    getOverrideDisplayLabel,
  }
}
